import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../lib/api";

// «Desplegar»: el servidor de producción de un proyecto del taller. Nunca viaja la contraseña ni el valor de una variable de vuelta.
export function useDeployStatus(projectId, enabled = true) {
  return useQuery({
    queryKey: ["deploy", projectId],
    queryFn: async () => (await apiClient.get(`/deploy/${projectId}`)).data,
    enabled: !!projectId && enabled,
    retry: false,
  });
}

function useDeployMutation(projectId, request, merge) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: (data) => queryClient.setQueryData(["deploy", projectId], (old) => merge(old || {}, data)),
  });
}

// Mira lo construido en el taller (solo lectura) y recomienda qué servidor contratar.
export function useRecommend(projectId) {
  return useDeployMutation(projectId, async () => (await apiClient.post(`/deploy/${projectId}/recommend`)).data, (old, rec) => ({ ...old, recommendation: rec }));
}

export function useSaveTarget(projectId) {
  return useDeployMutation(projectId, async (body) => (await apiClient.put(`/deploy/${projectId}/target`, body)).data, (old, view) => ({ ...old, ...view }));
}

export function useProbeTarget(projectId) {
  return useDeployMutation(projectId, async () => (await apiClient.post(`/deploy/${projectId}/probe`)).data, (old, view) => ({ ...old, ...view }));
}

export function useResetFingerprint(projectId) {
  return useDeployMutation(projectId, async () => (await apiClient.post(`/deploy/${projectId}/reset-fingerprint`)).data, (old, view) => ({ ...old, ...view }));
}

export function useRemoveTarget(projectId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () => (await apiClient.delete(`/deploy/${projectId}/target`)).data,
    onSuccess: (view) => queryClient.setQueryData(["deploy", projectId], (old) => ({ cloudflare_token: view.cloudflare_token, recommendation: old?.recommendation || null, configured: false, env: [] })),
  });
}

// El plan de publicación (lee el proyecto del taller en solo lectura; no toca el servidor de producción).
export function usePlan(projectId) {
  return useMutation({ mutationFn: async () => (await apiClient.post(`/deploy/${projectId}/plan`)).data });
}

// Variables secretas de la app: el valor se guarda cifrado y no vuelve nunca.
export function useSetEnv(projectId) {
  return useDeployMutation(projectId, async ({ name, value }) => (await apiClient.put(`/deploy/${projectId}/env/${name}`, { value })).data, (old, view) => ({ ...old, ...view }));
}

export function useRemoveEnv(projectId) {
  return useDeployMutation(projectId, async (name) => (await apiClient.delete(`/deploy/${projectId}/env/${name}`)).data, (old, view) => ({ ...old, ...view }));
}

// Avance de la publicación (se consulta cada 1,5 s mientras corre).
export function useRunState(projectId, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["deploy-run", projectId],
    queryFn: async () => {
      const data = (await apiClient.get(`/deploy/${projectId}/run`)).data;
      // al terminar, se vuelve a leer el estado general (donde está «ya publicado»): sin esto el paso del dominio seguía diciendo «primero publica»
      if (data?.run && data.run.status !== "running") queryClient.invalidateQueries({ queryKey: ["deploy", projectId] });
      return data;
    },
    enabled: !!projectId && enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data?.run?.status === "running" ? 1500 : false),
  });
}

// «Publicar ahora»: manda el hash del plan que el usuario confirmó.
export function usePublish(projectId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (planHash) => (await apiClient.post(`/deploy/${projectId}/run`, { plan_hash: planHash })).data,
    onSuccess: (data) => queryClient.setQueryData(["deploy-run", projectId], data),
  });
}

// Dominio y candado SSL (3B). El plan lee Cloudflare en solo lectura; el token vive en «Claves» y nunca llega a la pantalla.
export function useDomainPlan(projectId) {
  return useMutation({ mutationFn: async ({ proxied, strict, overwrite }) => (await apiClient.post(`/deploy/${projectId}/domain/plan`, { proxied: !!proxied, strict: !!strict, overwrite })).data });
}

export function useDomainRunState(projectId, enabled = true) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: ["deploy-domain-run", projectId],
    queryFn: async () => {
      const data = (await apiClient.get(`/deploy/${projectId}/domain/run`)).data;
      if (data?.run && data.run.status !== "running") queryClient.invalidateQueries({ queryKey: ["deploy", projectId] });
      return data;
    },
    enabled: !!projectId && enabled,
    retry: false,
    refetchInterval: (query) => (query.state.data?.run?.status === "running" ? 1500 : false),
  });
}

export function useDomainPublish(projectId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ planHash, proxied, strict, overwrite }) => (await apiClient.post(`/deploy/${projectId}/domain/run`, { plan_hash: planHash, proxied: !!proxied, strict: !!strict, overwrite })).data,
    onSuccess: (data) => queryClient.setQueryData(["deploy-domain-run", projectId], data),
  });
}

// «Revertir dominio»: los registros DNS y el modo SSL vuelven a como estaban.
export function useDomainRevert(projectId) {
  return useDeployMutation(projectId, async () => (await apiClient.post(`/deploy/${projectId}/domain/revert`)).data, (old, view) => ({ ...old, ...view }));
}

// «Publicar cambios» (Fase D): ¿lo publicado es igual a lo del taller? y qué versiones hay para deshacer. Cuelgan de ["deploy", id]: al terminar una publicación se vuelven a leer.
export function useChanges(projectId, enabled = true) {
  return useQuery({
    queryKey: ["deploy", projectId, "changes"],
    queryFn: async () => (await apiClient.get(`/deploy/${projectId}/changes`)).data,
    enabled: !!projectId && enabled,
    retry: false,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

export function useReleaseList(projectId, enabled = true) {
  return useQuery({
    queryKey: ["deploy", projectId, "releases"],
    queryFn: async () => (await apiClient.get(`/deploy/${projectId}/releases`)).data,
    enabled: !!projectId && enabled,
    retry: false,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });
}

// «¿Qué hago?» (Fase C): explicación + botones de una lista cerrada para el problema actual. No cambia nada.
export function useAdvice(projectId) {
  return useMutation({ mutationFn: async (body) => (await apiClient.post(`/deploy/${projectId}/advice`, body)).data });
}

// Versiones publicadas (3C): ver cuáles siguen en el servidor y volver a una anterior.
export function useReleases(projectId) {
  return useMutation({ mutationFn: async () => (await apiClient.get(`/deploy/${projectId}/releases`)).data });
}

export function useRollback(projectId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (release) => (await apiClient.post(`/deploy/${projectId}/rollback`, { release })).data,
    onSuccess: (data) => queryClient.setQueryData(["deploy-run", projectId], data),
  });
}
